import React, { useMemo } from 'react';
import { Tabs, Badge, Table, Tag, Space, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ToolOutlined, CheckCircleOutlined, CalendarOutlined, HistoryOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { MaintenanceRequests } from './MaintenanceRequests';
import { WorkOrders } from './WorkOrders';
import MaintenanceSchedulePage from './MaintenanceSchedulePage';
import { useCondoStore } from '../store/condoStore';
import type { MaintenanceRequest, WorkOrder, MaintenanceSchedule } from '../types';

const { Text } = Typography;

// ─── History Tab ─────────────────────────────────────────────────────────────
type HistoryRow = {
  id: string;
  _type: 'request' | 'work-order';
  title: string;
  unitNumber: string;
  status: string;
  closedDate: string;
};

const STATUS_COLORS: Record<string, string> = {
  completed: 'green',
  cancelled: 'default',
  COMPLETED: 'green',
  CANCELLED: 'default',
};

const historyColumns: ColumnsType<HistoryRow> = [
  {
    title: 'Type',
    dataIndex: '_type',
    key: 'type',
    width: 130,
    render: (t: string) =>
      t === 'request'
        ? <Tag color="blue" icon={<ToolOutlined />}>Request</Tag>
        : <Tag color="orange" icon={<CheckCircleOutlined />}>Work Order</Tag>,
    filters: [
      { text: 'Request', value: 'request' },
      { text: 'Work Order', value: 'work-order' },
    ],
    onFilter: (v, r) => r._type === v,
  },
  {
    title: 'Title',
    dataIndex: 'title',
    key: 'title',
    ellipsis: true,
  },
  {
    title: 'Unit',
    dataIndex: 'unitNumber',
    key: 'unit',
    width: 90,
    render: (u: string) => u ? <Text>{u}</Text> : <Text type="secondary">—</Text>,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    render: (s: string) => (
      <Tag color={STATUS_COLORS[s] ?? 'default'}>
        {s.toLowerCase() === 'completed' ? 'Completed' : 'Cancelled'}
      </Tag>
    ),
    filters: [
      { text: 'Completed', value: 'completed' },
      { text: 'Cancelled', value: 'cancelled' },
    ],
    onFilter: (v, r) => r.status.toLowerCase() === v,
  },
  {
    title: 'Closed Date',
    dataIndex: 'closedDate',
    key: 'closedDate',
    width: 130,
    render: (d: string) => d ? dayjs(d).format('MMM D, YYYY') : <Text type="secondary">—</Text>,
    sorter: (a, b) => dayjs(a.closedDate).unix() - dayjs(b.closedDate).unix(),
    defaultSortOrder: 'descend',
  },
];

const HistoryTabContent: React.FC<{ rows: HistoryRow[] }> = ({ rows }) => (
  <Table
    dataSource={rows}
    columns={historyColumns}
    rowKey="id"
    size="small"
    pagination={{ pageSize: 20, showTotal: (t) => `${t} items` }}
    locale={{ emptyText: 'No completed or cancelled items yet.' }}
  />
);

// ─── Main Hub ─────────────────────────────────────────────────────────────────
/**
 * S12 — Maintenance Hub
 * Unifies Requests → Work Orders → Schedules pipeline into one tabbed page.
 * Badge counts surface actionable items at a glance.
 */
const MaintenanceHub: React.FC = () => {
  const {
    maintenanceRequests = [],
    workOrders = [],
    maintenanceSchedules = [],
  } = useCondoStore() as any;

  // S12.2 — badge counts
  const openRequests: number = useMemo(
    () => (maintenanceRequests as MaintenanceRequest[])
      .filter(r => r.status !== 'completed' && r.status !== 'cancelled').length,
    [maintenanceRequests],
  );

  const activeOrders: number = useMemo(
    () => (workOrders as WorkOrder[])
      .filter(w => w.status !== 'COMPLETED' && w.status !== 'CANCELLED').length,
    [workOrders],
  );

  const overdueSchedules: number = useMemo(() => {
    const today = dayjs();
    return (maintenanceSchedules as MaintenanceSchedule[])
      .filter(s => s.status === 'ACTIVE' && dayjs(s.nextDueDate).isBefore(today)).length;
  }, [maintenanceSchedules]);

  // History tab: completed/cancelled from both sources, sorted newest first
  const historyRows: HistoryRow[] = useMemo(() => {
    const fromRequests: HistoryRow[] = (maintenanceRequests as MaintenanceRequest[])
      .filter(r => r.status === 'completed' || r.status === 'cancelled')
      .map(r => ({
        id: `req-${r.id}`,
        _type: 'request' as const,
        title: r.title,
        unitNumber: r.unitNumber ?? '',
        status: r.status,
        closedDate: r.completedDate ?? r.submittedDate ?? '',
      }));

    const fromOrders: HistoryRow[] = (workOrders as WorkOrder[])
      .filter(w => w.status === 'COMPLETED' || w.status === 'CANCELLED')
      .map(w => ({
        id: `wo-${w.id}`,
        _type: 'work-order' as const,
        title: w.title,
        unitNumber: w.unitNumber ?? '',
        status: w.status,
        closedDate: w.completedDate ?? w.createdAt ?? '',
      }));

    return [...fromRequests, ...fromOrders].sort(
      (a, b) => dayjs(b.closedDate).unix() - dayjs(a.closedDate).unix(),
    );
  }, [maintenanceRequests, workOrders]);

  const makeBadgeLabel = (icon: React.ReactNode, label: string, count: number) => (
    <Space size={4}>
      {icon}
      <span>{label}</span>
      {count > 0 && (
        <Badge
          count={count}
          size="small"
          style={{ backgroundColor: '#ff4d4f', marginLeft: 2 }}
        />
      )}
    </Space>
  );

  const items = [
    {
      key: 'requests',
      label: makeBadgeLabel(<ToolOutlined />, 'Requests', openRequests),
      children: <MaintenanceRequests />,
    },
    {
      key: 'work-orders',
      label: makeBadgeLabel(<CheckCircleOutlined />, 'Work Orders', activeOrders),
      children: <WorkOrders />,
    },
    {
      key: 'schedules',
      label: makeBadgeLabel(<CalendarOutlined />, 'Schedules', overdueSchedules),
      children: <MaintenanceSchedulePage />,
    },
    {
      key: 'history',
      label: (
        <Space size={4}>
          <HistoryOutlined />
          <span>History</span>
        </Space>
      ),
      children: <HistoryTabContent rows={historyRows} />,
    },
  ];

  return (
    <Tabs
      items={items}
      size="large"
      destroyInactiveTabPane={false}
      style={{ marginTop: -8 }}
    />
  );
};

export default MaintenanceHub;
